import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {LatLngBounds} from 'leaflet';
import {Observable, of, zip} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {map, switchMap, tap} from 'rxjs/operators';
import {checkLatValue, checkLonValue} from '../../dashboard-utility-functions';
import {ProjectBusinessLogicFilterModel} from '../../../shared/project-business-logic-filter/project-business-logic-filter.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardGeofenceService {

  protected itemCache: {[id: number]: any} = {};
  protected _itemsReceivedCount: number = null;
  protected _itemsTotalCount: number = null;
  constructor(
    protected http: HttpClient
  ) { }

  private readonly pageSize = 1000;

  public get itemsReceivedCount() {
    return this._itemsReceivedCount;
  }

  public get itemsTotalCount() {
    return this._itemsTotalCount;
  }

  clearCache() {
    this.itemCache = {};
  }

  private buildGeofenceSearchKey(key: string): string {
    return `GeofenceSearch[${key}]`;
  }

  public getItems(
    leafletBoundaryBox: LatLngBounds,
    projectBusinessLogicFilter: ProjectBusinessLogicFilterModel,
    extraParams: MapGeoDataParams | null,
  ): Observable<Array<any>> {
    let params = new HttpParams()
      .append('fields', 'id')
      .append('page', '1')
      .append('per-page', '1000')
      .append(this.buildGeofenceSearchKey('active'), '1')
      .append(this.buildGeofenceSearchKey('min_lat'), checkLatValue('min', leafletBoundaryBox.getSouth()).toString())
      .append(this.buildGeofenceSearchKey('min_lon'), checkLonValue('min', leafletBoundaryBox.getWest()).toString())
      .append(this.buildGeofenceSearchKey('max_lat'), checkLatValue('max', leafletBoundaryBox.getNorth()).toString())
      .append(this.buildGeofenceSearchKey('max_lon'), checkLonValue('max', leafletBoundaryBox.getEast()).toString());
    if (projectBusinessLogicFilter) {
      if (projectBusinessLogicFilter.project_id !== null) {
        params = params.append(this.buildGeofenceSearchKey('project_id'), projectBusinessLogicFilter.project_id.toString());
      }
      if (projectBusinessLogicFilter.business_logic_id !== null) {
        params = params.append(this.buildGeofenceSearchKey('business_logic_id'), projectBusinessLogicFilter.business_logic_id.toString());
      }
    }
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params = params.append(this.buildGeofenceSearchKey(key), value.toString());
        }
      });
    }
    return this.http.get(
      environment.apiBasepoint + 'geofence',
      {observe: 'response', params: params}
    ).pipe(
      switchMap(
        (response) => {
          const ids = (response.body as Array<any>).map(item => item.id);
          this._itemsTotalCount = parseInt(response.headers.get('X-Pagination-Total-Count'), 10);
          this._itemsReceivedCount = ids.length;
          const idsToFetch = ids.filter(id => !(id in this.itemCache));
          const cached = ids.filter(id => (id in this.itemCache)).map(id => this.itemCache[id]);
          let secondArrayObservable: Observable<Array<any>>;
          if (idsToFetch.length > 0) {
            secondArrayObservable = this.http.post<any>(
              environment.apiBasepoint + 'geofence-batch',
              {
                action: 'view',
                ids: idsToFetch,
                viewParams: {
                  fields: [
                    'id', 'project_country_id', 'name', 'description', 'type', 'radius', 'geometry',
                    'integrator_id', 'active', 'model_access_rights', 'region_id', 'project', 'projectCountry.country_code',
                    'region.name', 'technician_ids', 'integrator.login'
                  ].join(','),
                  expand: ['project', 'projectCountry', 'area', 'region', 'integrator'].join(','),
                  GeofenceSearch: {active: 1}
                }
              },
              {observe: 'body'}
            ).pipe(
              map(
                body => Object.values(body.result)
              ),
              tap(
                (geofences: Array<any>) => geofences.forEach(g => this.itemCache[g.id] = g)
              ),
            );
          } else {
            secondArrayObservable = of([]);
          }
          return zip(
            of(cached),
            secondArrayObservable
          );
        }
      ),
      map(
        ([arr1, arr2]) => [...arr1, ...arr2]
      )
    );
  }
}
